import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiUnreachableMessage } from '../config/api';
import { getFormPdfFileName } from '../config/safetyForms';
import { exportFormElementToPdf } from '../utils/formPdfExport';
import { extractFormSubmissionMeta } from '../utils/formSubmitMeta';
import { submitUserFormPdf } from '../utils/formSubmit';

const FormSubmitContext = createContext(null);

function toLocalDateTimeValue(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function yearMonthFromTimestamp(value) {
  if (!value) return { year: undefined, month: undefined };
  const match = String(value).match(/^(\d{4})-(\d{2})/);
  if (!match) return { year: undefined, month: undefined };
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  return {
    year: Number.isNaN(year) ? undefined : year,
    month: Number.isNaN(month) || month < 1 || month > 12 ? undefined : month,
  };
}

export function FormSubmitProvider({
  children,
  formRef,
  formIndex,
  formTitle = null,
  userEmail,
  companyLogoUrl = null,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [selectedSiteCompanyName, setSelectedSiteCompanyName] = useState('');
  const [selectedSiteCompanyLogoUrl, setSelectedSiteCompanyLogoUrl] = useState('');
  const [formTimestamp, setFormTimestamp] = useState(() => toLocalDateTimeValue());
  const [formImages, setFormImages] = useState([]);

  useEffect(() => {
    setFormImages([]);
  }, [formIndex]);

  const resolvedLogoUrl = selectedSiteCompanyLogoUrl || companyLogoUrl;

  const addFormImages = useCallback((files) => {
    const list = Array.from(files || []).filter((file) =>
      String(file.type || '').startsWith('image/')
    );
    if (!list.length) return;

    list.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : '';
        if (!dataUrl) return;
        setFormImages((prev) => {
          if (prev.length >= 12) return prev;
          return [
            ...prev,
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              name: file.name || 'image',
              dataUrl,
            },
          ];
        });
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const removeFormImage = useCallback((id) => {
    setFormImages((prev) => prev.filter((img) => img.id !== id));
  }, []);

  const clearFormImages = useCallback(() => {
    setFormImages([]);
  }, []);

  const submitForm = useCallback(async () => {
    const element = formRef?.current;
    if (!element) {
      setMessage({ type: 'error', text: 'Form not ready. Please wait a moment and try again.' });
      return;
    }
    if (!userEmail) {
      setMessage({ type: 'error', text: 'Unable to submit. Please log out and log in again.' });
      return;
    }
    if (!selectedSiteId) {
      setMessage({
        type: 'error',
        text: 'Please select a Location / Site before submitting.',
      });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const pdfBlob = await exportFormElementToPdf(element, {
        blank: false,
        companyLogoUrl: resolvedLogoUrl,
        userEmail,
        formTitle,
        attachmentImages: formImages.map((img) => img.dataUrl),
      });
      const fileName = getFormPdfFileName(formIndex);
      const meta = extractFormSubmissionMeta(element);
      const fromTs = yearMonthFromTimestamp(formTimestamp);
      await submitUserFormPdf({
        email: userEmail,
        fileName,
        pdfBlob,
        formIndex,
        siteProject: selectedSiteName || meta.site_project,
        supervisorName: meta.supervisor_name,
        year: fromTs.year || meta.year,
        month: fromTs.month || meta.month,
      });
      setMessage({
        type: 'success',
        text: 'Details saved',
      });
    } catch (err) {
      const text =
        err?.message?.toLowerCase().includes('fetch') ||
        err?.message?.toLowerCase().includes('network')
          ? apiUnreachableMessage()
          : err?.message || 'Failed to save form PDF';
      setMessage({ type: 'error', text });
    } finally {
      setSubmitting(false);
    }
  }, [
    formRef,
    formIndex,
    formTitle,
    userEmail,
    resolvedLogoUrl,
    selectedSiteName,
    selectedSiteId,
    formTimestamp,
    formImages,
  ]);

  const printForm = useCallback(async () => {
    const element = formRef?.current;
    if (!element) {
      setMessage({ type: 'error', text: 'Form not ready. Please wait a moment and try again.' });
      return;
    }

    setPrinting(true);
    setMessage(null);

    let objectUrl = null;
    let iframe = null;

    try {
      const pdfBlob = await exportFormElementToPdf(element, {
        blank: false,
        companyLogoUrl: resolvedLogoUrl,
        userEmail,
        formTitle,
        attachmentImages: formImages.map((img) => img.dataUrl),
      });
      objectUrl = URL.createObjectURL(pdfBlob);
      iframe = document.createElement('iframe');
      iframe.setAttribute('title', 'Print form');
      iframe.style.position = 'fixed';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.src = objectUrl;
      document.body.appendChild(iframe);

      await new Promise((resolve, reject) => {
        iframe.onload = () => resolve();
        iframe.onerror = () => reject(new Error('Failed to load print preview'));
      });

      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.message || 'Failed to print form',
      });
    } finally {
      setPrinting(false);
      window.setTimeout(() => {
        iframe?.remove();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }, 1000);
    }
  }, [formRef, formTitle, userEmail, resolvedLogoUrl, formImages]);

  const value = useMemo(
    () => ({
      submitForm,
      printForm,
      submitting,
      printing,
      message,
      setMessage,
      selectedSiteId,
      setSelectedSiteId,
      selectedSiteName,
      setSelectedSiteName,
      selectedSiteCompanyName,
      setSelectedSiteCompanyName,
      selectedSiteCompanyLogoUrl,
      setSelectedSiteCompanyLogoUrl,
      formTimestamp,
      setFormTimestamp,
      formImages,
      addFormImages,
      removeFormImage,
      clearFormImages,
    }),
    [
      submitForm,
      printForm,
      submitting,
      printing,
      message,
      selectedSiteId,
      selectedSiteName,
      selectedSiteCompanyName,
      selectedSiteCompanyLogoUrl,
      formTimestamp,
      formImages,
      addFormImages,
      removeFormImage,
      clearFormImages,
    ]
  );

  return (
    <FormSubmitContext.Provider value={value}>
      {children}
    </FormSubmitContext.Provider>
  );
}

export function useFormSubmit() {
  const ctx = useContext(FormSubmitContext);
  if (!ctx) {
    throw new Error('useFormSubmit must be used within FormSubmitProvider');
  }
  return ctx;
}
